import React, { useEffect, useState } from "react";
import { getBlogs } from "../util/http";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { marked } from "marked"; // For Markdown to HTML conversion
import Nav from "./Nav";
import Footer from "./Footer";
import {
  getClusterNarrativeForPost,
  getClusterResourcesForPost,
  shouldShowContentClusterPromos,
} from "../lib/contentClusters";
import { SEO_SHARE_IMAGES } from "../lib/seoImages";

// BlogPost: Public blog post page, loads by slug, SEO optimized
function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const all = await getBlogs();
      const now = Date.now();
      const found = (all || []).find((post) => {
        if (!post || post.slug !== slug || post.status !== "published" || !post.publishDate) {
          return false;
        }

        return new Date(post.publishDate).getTime() <= now;
      });
      setPost(found || null);
      setLoading(false);
    }
    fetch();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Nav />
        <main id="main-content" tabIndex="-1" className="container py-5">
          Loading...
        </main>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Nav />
        <main id="main-content" tabIndex="-1" className="container py-5">
          Post not found.
        </main>
        <Footer />
      </>
    );
  }

  // SEO meta tags and JSON-LD
  const url = `https://speakyourmenu.com/blog/${post.slug}`;
  const published = post.publishDate ? new Date(post.publishDate).toISOString() : undefined;
  const updated = post.lastUpdated ? new Date(post.lastUpdated).toISOString() : published;
  const tags = post.tags || [];
  const description = post.excerpt || post.description || post.body?.slice(0, 150) || "";
  const shareImage = SEO_SHARE_IMAGES.blog;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description,
    author: {
      "@type": "Person",
      name: post.author || "Speak Your Menu",
    },
    datePublished: published,
    dateModified: updated,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    image: [shareImage],
    keywords: tags.join(", "),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://speakyourmenu.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://speakyourmenu.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: url,
      },
    ],
  };
  const relatedResources = getClusterResourcesForPost(post);
  const clusterNarrative = getClusterNarrativeForPost(post);
  const showContentClusterPromos = shouldShowContentClusterPromos();

  return (
    <>
      <Nav />
      {/* Blog post styles for readability and accessibility */}
      <style>{`
        .blog-post {
          max-width: 700px;
          margin: 0 auto;
          font-size: 1.15rem;
          line-height: 1.7;
          color: #222;
          background: #fff;
          padding: 2rem 1.5rem;
          border-radius: 10px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.04);
        }
        .blog-post h1, .blog-post h2, .blog-post h3 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 700;
        }
        .blog-post p {
          margin-bottom: 1.25em;
        }
        .blog-post ul, .blog-post ol {
          margin-left: 2em;
          margin-bottom: 1.25em;
        }
        .blog-post blockquote {
          border-left: 4px solid #b00;
          background: #f9f9f9;
          margin: 1.5em 0;
          padding: 0.75em 1em;
          color: #555;
        }
        .blog-post a {
          color: #b00;
          text-decoration: underline;
        }
        .blog-post pre, .blog-post code {
          background: #f4f4f4;
          border-radius: 4px;
          padding: 0.2em 0.4em;
          font-size: 0.95em;
        }
        .blog-related-resources {
          margin-top: 2rem;
          padding: 1.25rem;
          border: 1px solid rgba(153, 0, 51, 0.12);
          border-radius: 18px;
          background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(251,247,248,0.95));
        }
        .blog-related-resources-grid {
          display: grid;
          gap: 0.9rem;
        }
        .blog-related-resource-card {
          padding: 1rem;
          border: 1px solid rgba(17,17,17,0.08);
          border-radius: 14px;
          background: white;
        }
        .blog-related-resource-title {
          margin: 0 0 0.35rem;
          font-size: 1rem;
          font-weight: 700;
        }
        .blog-related-resource-copy {
          margin: 0 0 0.55rem;
          font-size: 0.98rem;
          color: #555;
        }
        .blog-cluster-callout {
          margin: 0 0 1.5rem;
          padding: 1rem 1.1rem;
          border-radius: 18px;
          border: 1px solid rgba(153, 0, 51, 0.12);
          background: linear-gradient(180deg, rgba(251, 247, 248, 0.95), rgba(255,255,255,0.98));
        }
        .blog-cluster-eyebrow {
          margin: 0 0 0.35rem;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #990033;
        }
        .blog-cluster-copy {
          margin: 0;
          color: #555;
        }
        @media (max-width: 600px) {
          .blog-post {
            padding: 1rem 0.5rem;
            font-size: 1rem;
          }
        }
      `}</style>
      <Helmet>
        <title>{post.title} | Speak Your Menu Blog</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={post.title + " | Speak Your Menu Blog"} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title + " | Speak Your Menu Blog"} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={shareImage} />
        <meta name="author" content={post.author || "Speak Your Menu"} />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <main id="main-content" tabIndex="-1" className="container py-5 blog-post-page">
        <article className="blog-post">
          <Link to="/blog" className="btn btn-link mb-3">← Back to Blog</Link>
          <h1 className="mb-2">{post.title}</h1>
          <div className="text-secondary mb-3 small">
            {post.author && <span>By {post.author} | </span>}
            {post.publishDate && <span>{new Date(post.publishDate).toLocaleDateString()}</span>}
          </div>
          {showContentClusterPromos && clusterNarrative ? (
            <section className="blog-cluster-callout" aria-label="Menu training cluster context">
              <p className="blog-cluster-eyebrow">{clusterNarrative.eyebrow}</p>
              <p className="blog-cluster-copy">
                {clusterNarrative.intro}{" "}
                <Link to="/menu-training">Read the guide</Link>
                {" "}or{" "}
                <Link to="/menu-training/checklist">open the checklist</Link>.
              </p>
            </section>
          ) : null}
          <div className="mb-4" style={{ whiteSpace: "normal" }}>
            <div dangerouslySetInnerHTML={{ __html: marked.parse(post.body || "") }} />
          </div>
          {showContentClusterPromos && relatedResources.length > 0 ? (
            <section className="blog-related-resources" aria-label="Related menu training resources">
              <p className="text-uppercase small fw-bold mb-2" style={{ letterSpacing: "0.08em", color: "#990033" }}>
                Related resources
              </p>
              <div className="blog-related-resources-grid">
                {relatedResources.map((resource) => (
                  <div key={resource.to} className="blog-related-resource-card">
                    <h2 className="blog-related-resource-title">{resource.title}</h2>
                    <p className="blog-related-resource-copy">{resource.description}</p>
                    <Link to={resource.to}>{resource.cta}</Link>
                  </div>
                ))}
              </div>
              {clusterNarrative ? (
                <p className="blog-cluster-copy" style={{ marginTop: "0.85rem" }}>
                  {clusterNarrative.outro}
                </p>
              ) : null}
            </section>
          ) : null}
          {tags.length > 0 && (
            <div className="mb-3">
              {tags.map((tag) => (
                <span key={tag} className="badge bg-secondary me-1">{tag}</span>
              ))}
            </div>
          )}
          <div className="text-muted small">Last updated: {post.lastUpdated ? new Date(post.lastUpdated).toLocaleDateString() : post.publishDate && new Date(post.publishDate).toLocaleDateString()}</div>
        </article>
      </main>
      <Footer />
    </>
  );
}

export default BlogPost;
